import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { X, Loader2 } from 'lucide-react';
import { usePlant } from '../../../context/PlantContext';
import { useGardenSettings } from '../../../context/GardenSettingsContext';
import { plantsApi } from '../../../services/api';
import { applyAllFilters, buildGardenFilters, filterBySearch } from '../../../utils/plantFilters';
import { useSearchMissReporter } from '../../../hooks/useSearchMissReporter';
import PlantCatalogHeader from './PlantCatalogHeader';
import PlantCatalogSidebar from './PlantCatalogSidebar';
import PlantCatalogGrid from './PlantCatalogGrid';
import PlantDetailDialog from './PlantDetailDialog';

/**
 * Fresh, fully-populated empty filter set. Single source of truth for both the
 * initial state and every reset path so no category can silently persist across
 * opens (e.g. a flowering_months filter set via initialFilters — #662).
 */
const getEmptyFilters = () => ({
  sunlight: [],
  water_requirement: [],
  toxic: [],
  flower_color: [],
  evergreen: [],
  bee_friendly: [],
  frost_hardy: [],
  drought_tolerant: [],
  suitable_for_pot: [],
  suitable_for_planter: [],
  // High-priority filters
  care_growth_type: [],
  growth_speed: [],
  has_thorns: [],
  care_priority: [],
  is_climber: [],
  max_height_cm: null,
  ph_value: null,
  // Low-priority filters
  soil_type: [],
  toxicity_level: [],
  scent_intensity: [],
  root_type: [],
  winter_protection: [],
  flowering_months: null,
  safe_for_cats: [],
  safe_for_dogs: [],
});

/**
 * PlantCatalogDialog - Main plant catalog modal (1188×750px)
 *
 * Architecture:
 * - Fetches all plants once with complete properties (/plants/full)
 * - Filters client-side using useMemo for instant response
 * - Virtual scrolling for rendering optimization
 *
 * Layout: Sidebar (300px) + Content area
 * - Left: Collapsible filter sidebar (PlantCatalogSidebar)
 * - Right: Search, sort, and plant grid
 *
 * Features:
 * - Search (debounced 300ms in header)
 * - Multi-filter sidebar (6 sections, 25 filters)
 * - Sorting (name A-Z, Z-A)
 * - Virtual scroll (30 plants at a time)
 * - Place and Details actions
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether dialog is open
 * @param {Function} props.onClose - Callback to close dialog
 * @param {Function} props.onPlantPlace - Callback when plant placed
 * @param {Function} props.onPlantDetails - Callback when details requested
 * @param {Object} [props.initialFilters] - Filters to pre-apply when the dialog
 *   opens (e.g. a bloom-gap month → flowering_months range, #662). Merged over
 *   the empty defaults; cleared again on the next plain open.
 * @param {string} [props.initialSearchQuery] - Search text to restore on open
 *   (used when reopening after an ESC-cancelled placement, #764).
 * @param {string} [props.initialSortBy] - Sort order to restore on open (#764).
 */
const PlantCatalogDialog = ({
  isOpen,
  onClose,
  onPlantPlace,
  onPlantDetails,
  initialFilters = null,
  initialSearchQuery = null,
  initialSortBy = null
}) => {
  const { enterPlacementMode, markPlacementFromCatalog } = usePlant();
  const { settings } = useGardenSettings();

  // Garden filter toggle state
  const [gardenFilterActive, setGardenFilterActive] = useState(false);

  // Button only shown when wizard Step 1 is complete (soilType is set)
  const hasGardenSettings = settings?.soilType !== null;

  // Core state - all plants fetched once
  const [allPlants, setAllPlants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchTrigger, setFetchTrigger] = useState(0); // Used to force re-fetch on retry

  // Filter state — seeded with initialFilters so a dialog that mounts already
  // open (e.g. a bloom-gap jump, #662) is pre-filtered without waiting for the
  // closed→open transition below.
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');
  const [filters, setFilters] = useState(() => ({ ...getEmptyFilters(), ...(initialFilters || {}) }));
  const [sortBy, setSortBy] = useState(initialSortBy || 'name_asc');

  // Display state for virtual scrolling
  const [displayCount, setDisplayCount] = useState(30);
  const BATCH_SIZE = 30;

  // Detail dialog state
  const [detailPlant, setDetailPlant] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Trackers for render-phase "adjust state on change" logic (replaces effects
  // that called setState synchronously in their body).
  const [wasOpen, setWasOpen] = useState(isOpen);
  const [prevFilterState, setPrevFilterState] = useState({ searchQuery, filters, sortBy });

  // Memoized filtered plants - recalculates only when dependencies change
  const filteredPlants = useMemo(() => {
    return applyAllFilters(allPlants, filters, searchQuery, sortBy);
  }, [allPlants, filters, searchQuery, sortBy]);

  // Displayed plants (virtual scrolling)
  const displayedPlants = useMemo(() => {
    return filteredPlants.slice(0, displayCount);
  }, [filteredPlants, displayCount]);

  const hasMore = displayCount < filteredPlants.length;

  // Search-only match count (ignores sidebar filters) — feeds the anonymous
  // search-miss telemetry (#593) so only true DB gaps are logged, not
  // filter-induced empty results.
  const searchOnlyMatchCount = useMemo(
    () => filterBySearch(allPlants, searchQuery).length,
    [allPlants, searchQuery]
  );
  useSearchMissReporter({
    searchQuery,
    searchMatchCount: searchOnlyMatchCount,
    enabled: !loading && !error && allPlants.length > 0,
  });

  // Reset pagination when the filter inputs change — adjusted during render
  // rather than in an effect to avoid a setState-in-effect cascade.
  if (
    prevFilterState.searchQuery !== searchQuery ||
    prevFilterState.filters !== filters ||
    prevFilterState.sortBy !== sortBy
  ) {
    setPrevFilterState({ searchQuery, filters, sortBy });
    setDisplayCount(BATCH_SIZE);
  }

  // Fetch all plants when dialog opens (only if not already loaded)
  useEffect(() => {
    if (isOpen && allPlants.length === 0) {
      const fetchAllPlants = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await plantsApi.getFullCatalog();
          setAllPlants(response || []);
        } catch (err) {
          console.error('Failed to fetch plants:', err);
          setError('Pflanzen konnten nicht geladen werden. Bitte versuchen Sie es erneut.');
        } finally {
          setLoading(false);
        }
      };
      fetchAllPlants();
    }
  }, [isOpen, allPlants.length, fetchTrigger]); // fetchTrigger forces re-fetch on retry

  // Reset filters when the dialog opens (but keep allPlants cached) — adjusted
  // during render rather than in an effect to avoid a setState-in-effect cascade.
  // initialFilters (#662) are merged over the empty defaults so a caller can open
  // the catalog pre-filtered (e.g. a bloom-gap month); a plain open clears them.
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setSearchQuery(initialSearchQuery || '');
      setFilters({ ...getEmptyFilters(), ...(initialFilters || {}) });
      setSortBy(initialSortBy || 'name_asc');
      setDisplayCount(BATCH_SIZE);
      setError(null);
      setGardenFilterActive(false);
    }
  }

  // Load more for virtual scrolling
  const handleLoadMore = useCallback(() => {
    if (hasMore) {
      setDisplayCount(prev => prev + BATCH_SIZE);
    }
  }, [hasMore]);

  // Handle filter change
  const handleFilterChange = (category, values) => {
    setFilters(prev => ({
      ...prev,
      [category]: values
    }));
  };

  // Handle filter reset
  const handleResetFilters = () => {
    setFilters(getEmptyFilters());
    setGardenFilterActive(false);
  };

  // Toggle "Für meinen Garten" filter
  const handleToggleGardenFilter = () => {
    if (gardenFilterActive) {
      handleResetFilters();
    } else {
      const gardenFilters = buildGardenFilters(settings);
      setFilters(prev => ({ ...prev, ...gardenFilters }));
      setGardenFilterActive(true);
    }
  };

  // Handle place click
  const handlePlaceClick = (plant) => {
    // Remember the current catalog filter state so an ESC-cancelled placement can
    // reopen the catalog exactly where the user left off (#764). The dialog owns
    // this state; PlantCard already triggered enterPlacementMode by now.
    markPlacementFromCatalog({ filters, searchQuery, sortBy });

    if (onPlantPlace) {
      onPlantPlace(plant);
    } else {
      // Default behavior: enter placement mode and close dialog
      enterPlacementMode(plant);
      onClose();
    }
  };

  // Handle details click
  const handleDetailsClick = async (plant) => {
    if (onPlantDetails) {
      onPlantDetails(plant);
    } else {
      // Plant already has full properties, no need for extra API call
      setDetailPlant(plant);
      setDetailDialogOpen(true);
    }
  };

  // Handle detail dialog close
  const handleDetailDialogClose = () => {
    setDetailDialogOpen(false);
    setDetailPlant(null);
  };

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle ESC key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus trap
  const dialogRef = useRef(null);
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      const focusableElements = dialogRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- modal backdrop click-to-close */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
        onClick={handleOverlayClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="plant-catalog-title"
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- inner stopPropagation wrapper to prevent outer backdrop click */}
        <div
          ref={dialogRef}
          className="flex flex-col w-[1188px] h-[750px] bg-white rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
            <h2
              id="plant-catalog-title"
              className="text-lg font-semibold text-gray-900"
            >
              Pflanzenkatalog
            </h2>
            <button
              onClick={onClose}
              aria-label="Dialog schließen"
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Main Content */}
          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar */}
            <PlantCatalogSidebar
              filters={filters}
              onFilterChange={handleFilterChange}
              onResetFilters={handleResetFilters}
              gardenFilterActive={gardenFilterActive}
            />

            {/* Content Area */}
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Search Header */}
              <PlantCatalogHeader
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                sortBy={sortBy}
                onSortChange={setSortBy}
                resultsCount={filteredPlants.length}
                totalCount={allPlants.length}
                gardenFilterActive={gardenFilterActive}
                onToggleGardenFilter={handleToggleGardenFilter}
                hasGardenSettings={hasGardenSettings}
              />

              {/* Loading State */}
              {loading && (
                <div className="flex-1 flex flex-col items-center justify-center gap-3">
                  <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                  <p className="text-sm text-gray-600">Pflanzen werden geladen...</p>
                </div>
              )}

              {/* Error State */}
              {error && !loading && (
                <div className="flex-1 flex flex-col items-center justify-center gap-3 px-8">
                  <p className="text-sm text-red-600 text-center">{error}</p>
                  <button
                    onClick={() => {
                      setAllPlants([]);
                      setError(null);
                      setFetchTrigger(prev => prev + 1); // Increment to trigger re-fetch
                    }}
                    className="px-4 py-2 text-sm bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
                  >
                    Erneut versuchen
                  </button>
                </div>
              )}

              {/* Plant Grid */}
              {!loading && !error && (
                <PlantCatalogGrid
                  plants={displayedPlants}
                  hasMore={hasMore}
                  onLoadMore={handleLoadMore}
                  loading={false}
                  onPlaceClick={handlePlaceClick}
                  onDetailsClick={handleDetailsClick}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Plant Detail Dialog */}
      {isOpen && (
        <PlantDetailDialog
          isOpen={detailDialogOpen}
          onClose={handleDetailDialogClose}
          plant={detailPlant}
        />
      )}
    </>
  );
};

export default PlantCatalogDialog;
